import { useState, type FC, type FormEvent } from "react";
import { associateCertificatePolicy } from "../../stores/device_group_store";
import type { DeviceGroup } from "../../../../shared/models/device_group";

export const EditPolicyModal: FC<{
  group: DeviceGroup;
  policies: { id: string; name: string }[];
  onClose: () => void;
}> = ({ group, policies, onClose }) => {
  const [certificatePolicyId, setCertificatePolicyId] = useState(
    group.certificatePolicyId
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }
    setSubmitError(undefined);
    setIsSubmitting(true);
    try {
      await associateCertificatePolicy(group.id, certificatePolicyId);
      onClose();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Failed to update certificate policy.";
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vmx-modal">
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">Change Certificate Policy</div>
        <button
          className="vmx-btn vmx-btn-ghost vmx-btn-icon"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="vmx-modal-body">
          <div
            style={{
              padding: "12px 16px",
              background: "var(--vmx-gray-50)",
              borderRadius: "var(--vmx-radius)",
              marginBottom: 4,
            }}
          >
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--vmx-gray-500)",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 4,
              }}
            >
              Device Group
            </div>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: "var(--vmx-gray-800)",
              }}
            >
              {group.name}
            </div>
          </div>
          <div className="vmx-form-group">
            <label className="vmx-label">Select Certificate Policy</label>
            <select
              className="vmx-select"
              value={certificatePolicyId}
              onChange={(e) => setCertificatePolicyId(e.target.value)}
              disabled={isSubmitting}
            >
              {policies.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {submitError !== undefined && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: "0.8125rem",
                  color: "var(--vmx-danger)",
                }}
              >
                {submitError}
              </div>
            )}
          </div>
        </div>
        <div className="vmx-modal-footer">
          <button
            type="button"
            className="vmx-btn vmx-btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="vmx-btn vmx-btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Certificate Policy"}
          </button>
        </div>
      </form>
    </div>
  );
};
